import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  MenuItem,
  TextField,
  Typography,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { colors, fonts, size } from "../../ColorFont/color";
import { url } from "../../ConstUrl/Url";
import { Delete, Edit } from "@mui/icons-material";

const ManageCropCategory = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [categories, setCategories] = useState([]);
  const [existingCategories, setExistingCategories] = useState([]);
  const [categoryType, setCategoryType] = useState("");
  const [subCategories, setSubCategories] = useState([]);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [updatedCategoryType, setUpdatedCategoryType] = useState("");
  const [updatedSubCategories, setUpdatedSubCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [open, setOpen] = useState(false);
  const [displaySub, setDisplaySub] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  useEffect(() => {
    fetch(`${url}/api/get-categories`)
      .then((response) => response.json())
      .then((data) => {
        setCategories(data);
        setExistingCategories(data);
      })
      .catch((error) => setError(error.message));
  }, []);

  const handleAddCropCategory = (event) => {
    event.preventDefault();
    if (selectedCategory) {
      fetch(`${url}/api/add-category`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categoryType: selectedCategory, subCategories }),
      })
        .then((response) => response.json())
        .then((data) => {
          const existingCategoryIndex = categories.findIndex(
            (category) => category._id === data._id
          );
          if (existingCategoryIndex !== -1) {
            categories[existingCategoryIndex] = data;
          } else {
            setCategories([...categories, data]);
          }
          setSubCategories([]);
          setSuccess("Crop category added successfully!");
        })
        .catch((error) => setError(error.message));
    } else {
      fetch(`${url}/api/add-category`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ categoryType, subCategories }),
      })
        .then((response) => response.json())
        .then((data) => {
          setCategories([...categories, data]);
          setCategoryType("");
          setSubCategories([]);
          setSuccess("Crop category added successfully!");
        })
        .catch((error) => setError(error.message));
    }
  };

  const handleEditCategory = (id, categoryType, subCategories) => {
    setShowEditForm(true);
    setEditingCategoryId(id);
    setUpdatedCategoryType(categoryType);
    setUpdatedSubCategories(subCategories);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleClickOpen = () => {
    setOpen(true);
  };
 
  const handleDeleteCropCategory = (id) => {
    fetch(`${url}/api/delete-category/${id}`, {
      method: "DELETE",
    })
      .then((response) => response.json())
      .then((data) => {
        setCategories(categories.filter((category) => category._id !== id));
        setSuccess("Crop category deleted successfully!");
      })
      .catch((error) => setError(error.message));
  };

  return (
    <Box sx={{ margin: isMobile ? "10px" : "20px" }}>
      <Box sx={{ marginBottom: isMobile ? 3 : 5 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: fonts.poppins,
            fontWeight: 800,
            color: colors.primary_dark,
            mb: 1,
          }}
        >
          Manage Categories
        </Typography>
        <Typography
          sx={{
            fontFamily: fonts.poppins,
            color: colors.text,
            fontSize: "1rem",
          }}
        >
          Create, edit, or delete crop categories and subcategories.
        </Typography>
      </Box>

      <Box
        sx={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "flex-end",
          alignItems: "center",
          marginTop: 2,
          marginBottom: isMobile ? 3 : 5,
        }}
      >
        <Button
          variant="contained"
          onClick={handleClickOpen}
          sx={{
            fontFamily: fonts.poppins,
            backgroundImage: colors.button,
            width: isMobile ? "100%" : "auto",
          }}
        >
          Add New Category
        </Button>
      </Box>

      {/* Dialog for Adding New Category */}
      <Dialog open={open} onClose={handleClose} fullWidth={isMobile}>
        <DialogTitle
          sx={{
            fontFamily: fonts.poppins,
            fontWeight: 600,
            color: colors.primary_dark,
            fontSize: size.title,
          }}
        >
          Add Category
        </DialogTitle>

        <DialogContent>
          <Box
            component="form"
            onSubmit={handleAddCropCategory}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 3,
              padding: isMobile ? 2 : 4,
              borderRadius: 2,
              maxWidth: "1000px",
              margin: "auto",
              marginBottom: 5,
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexWrap: "wrap",
                flexDirection: isMobile ? "column" : "row",
                justifyContent: "space-between",
                gap: 3,
                width: "100%",
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: fonts.poppins,
                    fontSize: size.subtitle,
                    fontWeight: 600,
                    color: "#555",
                    marginBottom: 1,
                  }}
                >
                  Category Type
                </Typography>
                <FormControl fullWidth size="small">
                  <InputLabel>Select Category</InputLabel>
                  <Select
                    value={selectedCategory}
                    label="Select Category"
                    onChange={(event) =>
                      setSelectedCategory(event.target.value)
                    }
                  >
                    <MenuItem
                      value=""
                      onClick={() => {
                        setDisplaySub(true);
                      }}
                    >
                      Create New Category
                    </MenuItem>
                    {existingCategories.map((category) => (
                      <MenuItem
                        key={category._id}
                        value={category.categoryType}
                      >
                        {category.categoryType}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {displaySub && (
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: size.subtitle,
                      fontWeight: 600,
                      color: "#555",
                      marginBottom: 1,
                    }}
                  >
                    New Category
                  </Typography>
                  {selectedCategory === "" && (
                    <TextField
                      placeholder="Add new category"
                      value={categoryType}
                      onChange={(event) => setCategoryType(event.target.value)}
                      size="small"
                      fullWidth
                    />
                  )}
                </Box>
              )}
            </Box>

            {/* Subcategories Box */}
            <Box sx={{ width: "100%" }}>
              <Typography
                sx={{
                  fontFamily: fonts.poppins,
                  fontSize: size.subtitle,
                  fontWeight: 600,
                  color: "#555",
                  marginBottom: 1,
                }}
              >
                Subcategories
              </Typography>
              <TextField
                value={subCategories.join(", ")}
                onChange={(event) =>
                  setSubCategories(event.target.value.split(", "))
                }
                size="small"
                fullWidth
              />
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button
            type="submit"
            form="add-crop-category-form"
            variant="contained"
          >
            Add Category
          </Button>
        </DialogActions>
      </Dialog>

      {/* Category List */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {categories.map((category) => (
          <Box key={category._id} sx={{ width: "100%" }}>
            <Box
              sx={{
                padding: isMobile ? 2 : 4,
                borderRadius: 4,
                overflow: "hidden",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-8px)",
                  boxShadow: "0 12px 40px rgba(0, 0, 0, 0.12)",
                },
                position: "relative",
                background: "rgba(255, 255, 255, 0.8)",
                backdropFilter: "blur(12px)",
                border: `1px solid ${colors.border}`,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: isMobile ? "column" : "row",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: 3,
                }}
              >
                <Typography
                  sx={{
                    fontFamily: fonts.poppins,
                    fontSize: size.title,
                    fontWeight: 700,
                    color: colors.primary_dark,
                    textAlign: isMobile ? "center" : "left",
                  }}
                >
                  {category.categoryType}
                </Typography>

                <Box sx={{ display: "flex", gap: 1.5 }}>
                  <Tooltip title="Edit">
                    <Edit
                      onClick={() =>
                        handleEditCategory(
                          category._id,
                          category.categoryType,
                          category.subCategories
                        )
                      }
                      sx={{
                        color: colors.primary_dark,
                        fontSize: "1.5rem",
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>

                  <Tooltip title="Delete">
                    <Delete
                      onClick={() => handleDeleteCropCategory(category._id)}
                      sx={{
                        color: colors.primary_dark,
                        fontSize: "1.5rem",
                        cursor: "pointer",
                      }}
                    />
                  </Tooltip>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: 2,
                  marginBottom: 3,
                  flexDirection: isMobile ? "column" : "row",
                }}
              >
                {category.subCategories.map((subcategory) => (
                  <Box
                    key={subcategory}
                    sx={{
                      fontFamily: fonts.poppins,
                      fontSize: size.formLabel,
                      fontWeight: 500,
                      color: colors.text,
                      paddingLeft: 2,
                      borderLeft: `3px solid ${colors.primary}`,
                      width: isMobile ? "100%" : "calc(20% - 8px)",
                    }}
                  >
                    {subcategory}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default ManageCropCategory;
